import type {
  ProviderOwnerChecklistItemDto,
  ProviderOwnerOnboardingStatus,
  ProviderOwnerStatusDto,
} from "./provider-owner.types";

export const PROVIDER_OWNER_ONBOARDING_STATUSES: ProviderOwnerOnboardingStatus[] = [
  "LEAD_CREATED",
  "DRAFT_IN_PROGRESS",
  "AWAITING_PROVIDER_COMPLETION",
  "READY_FOR_BDO_REVIEW",
  "READY_FOR_OPS_APPROVAL",
  "APPROVED",
  "ACTIVATION_PENDING",
  "ACTIVE",
  "RETURNED_BY_BDO",
  "RETURNED_BY_OPS",
  "REJECTED",
];

export function isProviderOwnerEditable(
  status: ProviderOwnerOnboardingStatus,
): boolean {
  return [
    "LEAD_CREATED",
    "DRAFT_IN_PROGRESS",
    "AWAITING_PROVIDER_COMPLETION",
    "RETURNED_BY_BDO",
    "RETURNED_BY_OPS",
  ].includes(status);
}

export function isProviderOwnerUnderReview(
  status: ProviderOwnerOnboardingStatus,
): boolean {
  return ["READY_FOR_BDO_REVIEW", "READY_FOR_OPS_APPROVAL"].includes(status);
}

export function isProviderOwnerApproved(
  status: ProviderOwnerOnboardingStatus,
): boolean {
  return ["APPROVED", "ACTIVATION_PENDING", "ACTIVE"].includes(status);
}

export function isProviderOwnerRejected(
  status: ProviderOwnerOnboardingStatus,
): boolean {
  return status === "REJECTED";
}

export function getChecklistCompletionPercent(
  checklist: ProviderOwnerChecklistItemDto[],
): number {
  if (!checklist.length) return 0;
  const completed = checklist.filter((item) => item.completed).length;
  return Math.round((completed / checklist.length) * 100);
}

export function getBlockingChecklistCount(
  checklist: ProviderOwnerChecklistItemDto[],
): number {
  return checklist.filter((item) => item.blocking && !item.completed).length;
}

export function getNextActionLabel(
  status: ProviderOwnerOnboardingStatus,
  blockerCount: number,
): string {
  switch (status) {
    case "LEAD_CREATED":
      return "Start onboarding";
    case "DRAFT_IN_PROGRESS":
      return blockerCount > 0
        ? "Complete required fields"
        : "Submit for provider review";
    case "AWAITING_PROVIDER_COMPLETION":
      return "Wait for provider to finish";
    case "READY_FOR_BDO_REVIEW":
      return "Review in BDO queue";
    case "READY_FOR_OPS_APPROVAL":
      return "Review in Ops queue";
    case "RETURNED_BY_BDO":
      return "Address BDO feedback";
    case "RETURNED_BY_OPS":
      return "Address Ops feedback";
    case "APPROVED":
      return "Activate provider";
    case "ACTIVATION_PENDING":
      return "Complete activation";
    case "ACTIVE":
      return "Provider is live";
    case "REJECTED":
      return "No further action";
    default:
      return "Continue onboarding";
  }
}

export function buildProviderOwnerStatusDto(
  input: Omit<
    ProviderOwnerStatusDto,
    "overallCompletionPercent" | "pendingBlockers" | "nextActionLabel"
  >,
): ProviderOwnerStatusDto {
  const overallCompletionPercent = getChecklistCompletionPercent(input.checklist);
  const pendingBlockers = getBlockingChecklistCount(input.checklist);

  return {
    ...input,
    overallCompletionPercent,
    pendingBlockers,
    nextActionLabel: getNextActionLabel(input.status, pendingBlockers),
  };
}

export function makeChecklistItem(
  item: ProviderOwnerChecklistItemDto,
): ProviderOwnerChecklistItemDto {
  return item;
}